/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import filter from 'lodash.filter';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import isUndefined from 'lodash.isundefined';
import {WorkBook} from 'xlsx/types';
import {translate} from '@ion/core/src/locale/locale';
import {
  Entity
} from '@ion/core/src/types/entities';
import {
  DropsheetOptions,
  TemplateColumn
} from '@ion/core/src/types/templates';
import {idMap} from '@ion/core/src/utils/ObjectUtil';
import {isValidSpecialCharacter} from '@ion/core/src/utils/Validators';
import {
  normalizeAttributes,
  normalizeHeaders,
  process
} from '@ion/core/src/xlsx/ExcelHelpers';
import {
  dropsheet
} from '@ion/core/src/xlsx/importer';
import {ImportParamsType} from '@ion/dls/src/imports/BulkUploadDialog';

export interface BulkEntitiesImportParamsType extends ImportParamsType {
  workbook?: WorkBook;
}
/**
 * Hook to encapsulate import functionality for Bulk Action for different entities.
 */
export const useBulkSelectedEntitiesImport = ({
  entity,
  setImportStatus,
  setIsEmptyFile,
  setIsInvalidFormat,
  isBulkAction
}) => {
  const templateColumns: TemplateColumn[] = translate(`common:editors.bulkActions.dialogBulkAction.template.${entity}`, {returnObjects: true});
  const attributes                        = idMap(templateColumns, 'header', 'key');
  const importResults                     = useSelector(({importer: {current}}) => current);
  const headers                           = Object.keys(attributes);
  const isDeviceModule                    = entity === Entity.device;
  const isAssetModule                     = entity === Entity.asset;
  const [importParams, setImportParams]   = useState<BulkEntitiesImportParamsType>({
    items      : [],
    isComplete : false,
    isFailure  : false,
    workbook   : null
  });

  useEffect(() => {
    importerOptions.target = document.getElementById('entity-importer');
    dropsheet(importerOptions, setImportStatus);
  }, []);

  const handleImport = function(files: FileList): void {
    setImportStatus(true);
    process(files, importerOptions, false);
  };

  /**
   * Runs upon successful completion of the upload of the file containing entity identifiers.
   */
  const onImportCompleted = function(
    items: Array<any>,
    workbook: WorkBook,
    files: Array<any>
  ) {
    const file = files ? files[0] : {};
    let errorCheck = false;
    const mandatoryCols = filter(templateColumns, {required: true});
    for (const item of items) {
      for (const col of mandatoryCols) {
        if (isNil(item[col.key])) {
          errorCheck = true;
          break;
        }
      }
      if (errorCheck) {
        setIsInvalidFormat(errorCheck);
        break;
      }
    }
    if (errorCheck) {
      return onImportFailed();
    }

    if (isBulkAction) {
      let isValidItems;
      if (isDeviceModule) {
        isValidItems = items?.find(({deviceIdentifier}) => isValidSpecialCharacter(deviceIdentifier));
      } else {
        isValidItems = items?.find(({assetIdentifier}) => isValidSpecialCharacter(assetIdentifier));
      }
      if (isEmpty(workbook) && !isUndefined(isValidItems)) {
        return setIsInvalidFormat(true);
      } else {
        setIsInvalidFormat(false);
      }
    }

    setIsEmptyFile(isEmpty(items));

    setImportParams(previousImportParams => ({
      ...previousImportParams,
      fileName   : file?.name,
      fileSize   : file?.size,
      isComplete : !isEmpty(items),
      isFailure  : false,
      workbook   : file,
      items
    }));
  };

  /**
   * Runs upon a failed upload of the file containing entity identifiers.
   */
  const onImportFailed = function(e?: unknown) {
    setImportParams(previousImportParams => ({
      ...previousImportParams,
      isComplete : false,
      isFailure  : true,
      workbook   : null
    }));
  };

  const converter = (column: string): string => {
    const attrs   = normalizeAttributes(attributes);
    const extract = isDeviceModule ? column !== headers[5] : true;
    const header  = normalizeHeaders(column, attrs, extract);
    return isDeviceModule ? header || column : header;
  };

  const [importerOptions, setImporterOptions] = useState<DropsheetOptions>({
    onComplete  : onImportCompleted,
    onFailed    : onImportFailed,
    onSizeError : onImportFailed,
    parse       : true,
    isAssetBulk : isAssetModule,
    converter
  });

  return {
    templateColumns,
    importParams,
    importResults,
    handleImport
  };
};

export default useBulkSelectedEntitiesImport;
